// react
import { MouseEvent, useContext, useState } from 'react';

// types
import { CashInMethod, Feedback } from '../../../@types';

// i18n
import { Trans, useTranslation } from 'react-i18next';

// router
import { useSearchParams } from 'react-router-dom';

// context
import { ContextBanner } from '../../../App.context';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// mui
import { Alert, AlertTitle, Grid, Typography } from '@mui/material';

// dialogs
import { DialogActions } from '../DialogActions/DialogActions';

// ui
import { Loading } from '../../ui/Loading/Loading';

// props
export interface DialogDeleteCashInMethodProps {
	isOpen: boolean;
	onClose: (e?: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
	onSuccess?: () => void;
}

export const DialogDeleteCashInMethod = ({
	isOpen,
	onClose,
	onSuccess,
}: DialogDeleteCashInMethodProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// hooks
	const [searchParams] = useSearchParams();
	const { t } = useTranslation();

	// params
	const paramCashInMethod = searchParams.get('dialogCashInMethod');
	const paramDialog = searchParams.get('dialog');

	// context
	const { setBanner } = useContext(ContextBanner);

	// state
	const [cashInMethod, setCashInMethod] = useState<CashInMethod | null>(null);
	const [feedback, setFeedback] = useState<Feedback | null>(null);

	// fetch cash-in method
	const { isLoading: isLoadingCashInMethod } = useFetch({
		isEnabled: Boolean(paramCashInMethod && paramDialog === 'deleteCashInMethod'),
		url: `${process.env.REACT_APP_API_URL}/cash-in-methods/${paramCashInMethod}`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setCashInMethod(res.data[0]);
			}
		},
	});

	// delete cash-in method
	const { fetchRequest: deleteCashInMethod, isLoading: isLoadingDeleteCashInMethod } = useFetch({
		isLazy: true,
		url: `${process.env.REACT_APP_API_URL}/cash-in-methods/${paramCashInMethod}`,
		options: {
			method: 'DELETE',
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onError: (error) => {
			const feedbackNew: Feedback = {
				alert: {
					children: t('error.default'),
				},
			};

			if (error.id === 'CASH_IN_METHOD_DEFAULT_CANNOT_DELETE') {
				feedbackNew.alert.children = t('error.cashInMethodDefaultCannotDelete');
				feedbackNew.title = {
					children: t('cashInMethodCannotBeDeleted'),
				};
			}

			if (error.id === 'CASH_IN_METHOD_UNAUTHORIZED') {
				feedbackNew.alert.children = t('error.unauthorized');
			}

			setFeedback(feedbackNew);
		},
		onSuccess: () => {
			// set success banner
			setBanner({
				hasClose: true,
				message: t('alert.cashInMethodDeleted', { name: cashInMethod?.name }),
				severity: 'success',
			});

			// close dialog
			onClose();

			// refresh table
			if (onSuccess) {
				onSuccess();
			}
		},
	});

	// handlers
	const handleSubmit = (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
		// clear feedback
		setFeedback(null);

		// delete cash-in method
		deleteCashInMethod();
	};

	const handleClose = (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
		// clear feedback
		setFeedback(null);

		if (onClose) {
			onClose(e);
		}
	};

	const renderContent = () => {
		if (isLoadingCashInMethod) {
			return (
				<Grid item xs={12}>
					<Grid alignItems="center" container justifyContent="center">
						<Grid item>
							<Loading />
						</Grid>
					</Grid>
				</Grid>
			);
		}

		return (
			<>
				<Grid item xs={12}>
					<Trans
						components={{
							strong: <Typography color="brand.blue.600" component="span" fontWeight={600} />,
						}}
						i18nKey="dialogDeleteCashInMethod.content"
						values={{ name: cashInMethod?.name }}
					/>
				</Grid>
				<Grid item xs={12}>
					<Alert severity="warning">{t('alert.deleteCashInMethod')}</Alert>
				</Grid>
				{feedback && (
					<Grid item xs={12}>
						<Alert severity="error" {...feedback.alert}>
							{feedback.title && <AlertTitle {...feedback.title} />}
							{feedback.alert.children}
						</Alert>
					</Grid>
				)}
			</>
		);
	};

	return (
		<DialogActions
			actions={[
				{
					children: t('cancel'),
					onClick: handleClose,
					variant: 'text',
				},
				{
					children: t('deleteCashInMethod'),
					color: 'error',
					loading: isLoadingDeleteCashInMethod,
					onClick: handleSubmit,
					variant: 'contained',
				},
			]}
			alignItems="center"
			hasClose={true}
			IconCircleProps={{
				type: 'payment',
			}}
			isOpen={isOpen}
			maxWidth={400}
			onClose={handleClose}
			subtitle={{
				children: isLoadingCashInMethod ? '' : cashInMethod?.name,
			}}
			title={{
				children: t('deleteCashInMethod'),
			}}>
			<Grid container spacing={2}>
				{renderContent()}
			</Grid>
		</DialogActions>
	);
};
